import { useState, useEffect } from "react";
import { Alert, Button, Form, Modal, ListGroup, Spinner } from "react-bootstrap";
import {
    deleteNewProductCategory,
    updateNewProductCategory,
    getNewPlaces,
    getNewCategoryPlaces
} from "../../apiForNewProducts";

export default function EditNewCategory({
    show,
    onHide,
    jwt,
    onUpdate,
    onDeleted,
    categories,
    category,
    places = []
}) {
    const [categoryLocal, setCategoryLocal] = useState({ ...category });
    const [onDelete, setOnDelete] = useState(null);
    const [selectedPlaceIds, setSelectedPlaceIds] = useState([]);
    const [isLoadingPlaces, setIsLoadingPlaces] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (show && category?.id) {
            loadCategoryPlaces();
        } else {
            resetForm();
        }
    }, [show, category?.id]);

    const loadCategoryPlaces = async () => {
        setIsLoadingPlaces(true);
        try {
            const categoryPlaces = await getNewCategoryPlaces(category.id, jwt);
            setSelectedPlaceIds(categoryPlaces?.map(p => p.newProductPlaceId) || []);
        } catch (error) {
            console.error("Ошибка загрузки платформ:", error);
            alert("Не удалось загрузить привязанные платформы");
        } finally {
            setIsLoadingPlaces(false);
        }
    };

    const togglePlaceSelection = (placeId) => {
        setSelectedPlaceIds(prev =>
            prev.includes(placeId)
                ? prev.filter(id => id !== placeId)
                : [...prev, placeId]
        );
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const response = await updateNewProductCategory(
                categoryLocal.id,
                categoryLocal.name,
                categoryLocal.priority,
                jwt,
                selectedPlaceIds.length > 0 ? selectedPlaceIds : undefined
            );

            if (response) {
                const updatedCategory = {
                    ...categoryLocal,
                    places: places
                        .filter(p => selectedPlaceIds.includes(p.id))
                        .map(p => ({
                            newProductPlaceId: p.id,
                            name: p.name
                        }))
                };
                onUpdate(updatedCategory);
                onHide();
            } else {
                alert('Не удалось обновить, разрыв с сервером');
            }
        } catch (error) {
            console.error("Ошибка обновления категории:", error);
            alert('Ошибка при обновлении категории');
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setCategoryLocal(category ? { ...category } : {});
        setSelectedPlaceIds([]);
        setOnDelete(null);
    };

    const handleClose = () => {
        resetForm();
        onHide();
    };

    const handleDelete = async () => {
        if (!onDelete) return;

        try {
            await deleteNewProductCategory(
                category.id,
                onDelete.statusProducts,
                onDelete.newCategory,
                jwt
            );
            onDeleted(onDelete);
            setOnDelete(null);
        } catch (error) {
            console.error("Ошибка удаления категории:", error);
            alert('Ошибка при удалении категории');
        }
    };

    if (onDelete) {
        return (
            <Modal centered show={onDelete != null} onHide={() => setOnDelete(null)}>
                <Form onSubmit={e => e.preventDefault()}>
                    <Modal.Header closeButton>
                        <Modal.Title>Удаление категории</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Alert variant="warning">
                            Вы уверены, что хотите удалить категорию "{category.name}"?
                        </Alert>

                        <Form.Group className="mb-3">
                            <Form.Label>Что делать с продукцией в этой категории?</Form.Label>
                            <Form.Select
                                id='category-move-delete'
                                value={onDelete.statusProducts}
                                onChange={(e) => setOnDelete({ ...onDelete, statusProducts: e.target.value })}
                            >
                                <option value={'delete'}>Удалить продукцию и ее отправки</option>
                                <option value={'move'}>Переместить в другую категорию</option>
                            </Form.Select>
                        </Form.Group>

                        {onDelete.statusProducts === 'move' && (
                            <Form.Group className="mb-3">
                                <Form.Label>Выберите новую категорию</Form.Label>
                                <Form.Select
                                    required
                                    id='category-move-new-category'
                                    value={onDelete.newCategory || ''}
                                    onChange={(e) => setOnDelete({
                                        ...onDelete,
                                        newCategory: e.target.value ? parseInt(e.target.value) : null
                                    })}
                                >
                                    <option value="">Выберите категорию</option>
                                    {categories && categories
                                        .filter(f => f.id !== category.id)
                                        .map(p => (
                                            <option key={p.id} value={p.id}>
                                                {p.name}
                                            </option>
                                        ))
                                    }
                                </Form.Select>
                                {!onDelete.newCategory && (
                                    <Form.Text className="text-danger">
                                        Пожалуйста, выберите категорию для перемещения продукции
                                    </Form.Text>
                                )}
                            </Form.Group>
                        )}

                        {onDelete.statusProducts === 'delete' && (
                            <Alert variant="danger">
                                Внимание! Это действие удалит всю продукцию в этой категории
                                и все связанные отправки. Отменить это действие будет невозможно.
                            </Alert>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setOnDelete(null)}>
                            Отмена
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleDelete}
                            disabled={onDelete.statusProducts === 'move' && !onDelete.newCategory}
                        >
                            Удалить
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        );
    }

    const listItemStyle = {
        cursor: 'pointer',
        userSelect: 'none',
        outline: 'none'
    };

    return (
        <Modal
            centered
            show={show}
            onHide={handleClose}
            size="lg"
            backdrop="static"
            keyboard={false}
        >
            <Form onSubmit={onSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title>Редактирование категории: {category.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Название категории</Form.Label>
                        <Form.Control
                            required
                            type="text"
                            value={categoryLocal.name}
                            onChange={(e) => setCategoryLocal({
                                ...categoryLocal,
                                name: e.target.value
                            })}
                            disabled={isSubmitting}
                        />
                    </Form.Group>


                    <Form.Group className="mb-3">
                        <Form.Text className="text-muted d-block mb-2">
                            Если не выбрано ни одной платформы, категория будет доступна на всех платформах.
                        </Form.Text>

                        {isLoadingPlaces ? (
                            <div className="text-center py-3">
                                <Spinner animation="border" />
                                <p className="mt-2">Загрузка платформ...</p>
                            </div>
                        ) : places.length > 0 ? (
                            <ListGroup style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                {places.map(place => (
                                    <ListGroup.Item
                                        key={place.id}
                                        as="div"
                                        role="button"
                                        tabIndex={0}
                                        active={selectedPlaceIds.includes(place.id)}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            togglePlaceSelection(place.id);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                togglePlaceSelection(place.id);
                                            }
                                        }}
                                        disabled={isSubmitting}
                                        style={listItemStyle}
                                        className="d-flex justify-content-between align-items-center"
                                    >
                                        <div>
                                            <strong>{place.name}</strong>
                                        </div>
                                        {selectedPlaceIds.includes(place.id) && (
                                            <span className="text-success fs-5">✓</span>
                                        )}
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        ) : (
                            <Alert variant="info">Нет доступных платформ</Alert>
                        )}
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="danger"
                        onClick={() => {
                            const otherCategory = categories.find(c => c.id !== category.id);
                            setOnDelete({
                                statusProducts: 'move',
                                newCategory: otherCategory ? otherCategory.id : null
                            });
                        }}
                        disabled={isSubmitting}
                    >
                        Удалить
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={handleClose}
                        disabled={isSubmitting}
                    >
                        Отмена
                    </Button>
                    <Button
                        variant="success"
                        type="submit"
                        disabled={isSubmitting || !categoryLocal.name.trim()}
                    >
                        {isSubmitting ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Сохранение...
                            </>
                        ) : 'Сохранить'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}